import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { ActivatedRoute, NavigationExtras, ParamMap } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { isEmpty, noop } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  IEntity,
  ISearchParams,
  SEARCH_LIST_FILTER,
  SearchParamsEnum,
  SortByEnum,
  SortOrderEnum,
} from '../../../entity-data/entity-data.data';
import { IMasterListHeader } from '../master-list/master-list.interface';
import { INoItemsOptions } from '../../../feed/feed.interface';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { SearchListBuilder } from './search-list.builder';
import { NavigationService } from '../../../navigation/navigation.service';
import { IEntityStore } from '../../../entity-data/entity-store.interface';
import { IEntityDataDispatcher } from '../../../entity-data/entity-data-dispatcher.interface';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { GlobalSearchService } from '../../../global-search/global-search.service';

@Component({
  selector: 'app-search-list',
  templateUrl: 'search-list.component.html',
  styleUrls: ['search-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [SearchListBuilder],
})
export class SearchListComponent implements OnInit, OnDestroy {
  @Input() entityDataDispatcher: IEntityDataDispatcher;
  @Input() entityDataStore: IEntityStore;
  @Input() headers: List<IMasterListHeader>;
  @Input() defaultSearchParams: ISearchParams;
  @Input() noItemsDescriptionKey: string;
  @Input() relevancySearchMode: boolean;
  @Input() enableBulkActions: boolean;
  @Input() groupByFn: Function;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  @ContentChild(TemplateRef, { static: false }) template: TemplateRef<any>;

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _errorBannerId: string;

  listFilter: string;
  results$: Observable<List<IEntity>>;
  fetchingList$: Observable<boolean>;
  lastPageFetched$: Observable<boolean>;
  noResultsOptions$: BehaviorSubject<INoItemsOptions> = new BehaviorSubject<INoItemsOptions>(
    void 0
  );
  sortByHeader$: BehaviorSubject<IMasterListHeader> = new BehaviorSubject<IMasterListHeader>(
    void 0
  );

  constructor(
    private _route: ActivatedRoute,
    private _ngrxSearchListBuilder: SearchListBuilder,
    private _translateService: TranslateService,
    private _globalBannerService: GlobalBannerService,
    private _navigationService: NavigationService,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _featuresDataStore: FeaturesDataStore,
    private _globalSearchService: GlobalSearchService
  ) {}

  ngOnInit() {
    this.listFilter = SEARCH_LIST_FILTER;

    const searchParams$ = this.entityDataStore.getSearchParams(this.listFilter);

    const isIaNavEnabled$ = this._featuresDataStore
      .getFeatureToggles()
      .pipe(
        map((toggles) => toggles.isAllAccess(Toggles.KEYS.IA_NAV_EXPERIMENT))
      );

    // results
    this.results$ = this.entityDataStore.getSortedList(this.listFilter);

    // update fetchingList
    this.fetchingList$ = this.entityDataStore
      .getListFetched(this.listFilter)
      .pipe(map((fetched: boolean) => !fetched));

    // update lastPageFetched
    this.lastPageFetched$ = searchParams$.pipe(
      map((searchParams: ISearchParams) => !searchParams.next_page_url)
    );

    // triggers search list
    this._route.queryParamMap
      .pipe(takeUntil(this._destroyed))
      .subscribe((queryParamMap: ParamMap) => {
        const query = queryParamMap.get(SearchParamsEnum.query);
        const sortBy = queryParamMap.get(
          SearchParamsEnum.sort_by
        ) as SortByEnum;
        const sortOrder = queryParamMap.get(
          SearchParamsEnum.sort_order
        ) as SortOrderEnum;
        const isSortingMissing = !sortBy || !sortOrder;

        if (!this.relevancySearchMode && isSortingMissing) {
          const queryParams: ISearchParams = {
            sort_by: sortBy || this.defaultSearchParams.sort_by,
            sort_order: sortOrder || this.defaultSearchParams.sort_order,
          };
          const navigationExtras: NavigationExtras = {
            queryParams,
            queryParamsHandling: 'merge',
            replaceUrl: true,
          };

          this._navigationService.goTo([], navigationExtras);
        } else {
          this.entityDataDispatcher.searchList(
            query,
            sortBy || this.defaultSearchParams.sort_by,
            sortOrder || this.defaultSearchParams.sort_order,
            true,
            this.relevancySearchMode,
            !this.relevancySearchMode
          );
        }
      });

    // on search success:
    // - track searched event
    // - update no results message and sortByHeader
    this.results$
      .pipe(
        takeUntil(this._destroyed),
        filter((x) => !!x),
        withLatestFrom(searchParams$, isIaNavEnabled$)
      )
      .subscribe(([searchResults, searchParams, isIaNavEnabled]) => {
        const resultsSize = (searchResults || List()).size;
        const trackingAction = this._trackingActionBuilder.buildSearched(
          searchParams.query,
          resultsSize,
          this.trackingScreenName
        );

        this._trackingDispatcher.trackEvent(
          this.trackingObject,
          trackingAction,
          this.trackingAdditionalProperties
        );

        if (isIaNavEnabled) {
          this.noResultsOptions$.next(
            this._globalSearchService.emptyTabMessage.value
          );
        } else {
          this.noResultsOptions$.next(
            !resultsSize
              ? {
                  description: this._translateService.instant(
                    this.noItemsDescriptionKey,
                    {
                      query: searchParams[SearchParamsEnum.query],
                    }
                  ),
                }
              : null
          );
        }

        this.sortByHeader$.next(
          this._ngrxSearchListBuilder.buildSortByHeader(
            this.headers,
            searchParams
          )
        );
      }, noop);

    // on search error:
    // - track searched event
    // - show error banner
    this.entityDataStore
      .getListError(this.listFilter)
      .pipe(takeUntil(this._destroyed), withLatestFrom(searchParams$))
      .subscribe(([searchError, searchParams]) => {
        if (searchError) {
          const trackingAction = this._trackingActionBuilder.buildSearched(
            searchParams.query,
            0,
            this.trackingScreenName
          );

          this._trackingDispatcher.trackEvent(
            this.trackingObject,
            trackingAction
          );

          this._errorBannerId = this._globalBannerService.showSystemErrorBanner(
            searchError.error,
            null,
            () => {
              this.entityDataDispatcher.clearList(this.listFilter);

              this._errorBannerId = null;
            }
          );
        } else {
          if (this._errorBannerId) {
            this._globalBannerService.hideBanner(this._errorBannerId);

            this._errorBannerId = null;
          }
        }
      });
  }

  sortList(header: IMasterListHeader) {
    if (!this.relevancySearchMode) {
      this.sortByHeader$.next(header);

      this.entityDataStore
        .getSearchParams(this.listFilter)
        .pipe(
          takeUntil(this._destroyed),
          map(
            (searchParams: ISearchParams) => searchParams && searchParams.query
          ),
          take(1)
        )
        .subscribe((query: string) => {
          const sortParams: ISearchParams = this._ngrxSearchListBuilder.buildSortParams(
            header.sortBy,
            header.sortOrder
          );
          const params: ISearchParams = {
            ...(!isEmpty(query) ? { query } : {}),
            ...sortParams,
          };

          this._navigationService.goTo([], {
            queryParams: params,
            relativeTo: this._route,
          });
        });
    }
  }

  fetchNextPage() {
    this.entityDataDispatcher.fetchNextPage(this.listFilter);
  }

  ngOnDestroy() {
    this.entityDataDispatcher.clearList(this.listFilter);

    if (this._errorBannerId) {
      this._globalBannerService.hideBanner(this._errorBannerId);

      this._errorBannerId = null;
    }

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
